import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  OrganisationContext,
  ReportsDownloadUpdate,
} from "../../../layouts/MainLayout";

/**Third Part Libraries */
import moment from "moment-timezone";
import { useAccordionButton } from "react-bootstrap/AccordionButton";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

/**Custom Components**/
import DateCalendar from "../../../utils/components/common/date_time_picker/DateCalendar";
import LoadingSpinner from "../../../utils/components/common/loadingSpinner/LoadingSpinner";
import ModalComponent from "../../../utils/components/common/modalComponent/ModalComponent";
import SearchBox from "../../../utils/components/common/searchBox/deviceSearchBox";
import Icons from "../../../utils/components/icons";
import DeviceGraphReport from "./DeviceGraphReport";

/**Services and generic functions**/
import { generateCSVReport } from "../../../services/ReportService";
import { formatAMPM } from "../../../utils/utils";

/**Style**/
import "../ReportDetails.scss";
import ReportListViewTab from "./ReportListViewTab";

const DeviceDiagnoParameterReport = () => {
  const { t } = useTranslation("reports");

  /**Location related */
  const navigate = useNavigate();
  const location = useLocation();
  const currentRoute = location?.pathname?.split("/")[2] || "";
  const locationStateData = location?.state || null; // Check if it is redirected from Device-detail-page, to pre-select the device

  /**Contexts */
  const { siteData } = useContext(OrganisationContext);
  const { reportUpdateStatus } = useContext(ReportsDownloadUpdate);

  /**Calendar related states starts */
  const [durationErr, setDurationErr] = useState({
    isErr: false,
    errMsg: "",
  });
  const [selectedDuration, setSelectedDuration] = useState([
    new Date().toISOString(),
    new Date(new Date() - 1000 * 60 * 60).toISOString(),
  ]);
  /**Calendar related states ends */

  const searchBoxRef = useRef();
  const [selectedDevice, setSelectedDevice] = useState();
  const [selectedDeviceDetails, setSelectedDeviceDetails] = useState();
  const [refreshTime, setRefreshTime] = useState(new Date());
  const [reportData, setReportData] = useState(); // If reportData is availabel then only show refresh time and enable csv btn
  const [filtersApplied, setFiltersApplied] = useState(); // For generateCSVReport query Param
  const [isLoading, setIsLoading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  /**Accordion State */
  const [activeAccordion, setActiveAccordion] = useState({
    listViewActive: true,
    graphViewActive: true,
  });

  /**Go back to device view**/
  const goBack = useCallback(() => {
    navigate("/reports");
  }, [navigate]);

  /**Update the download icon on new report download mqtt event**/
  const newDownloadNotification = useMemo(() => {
    const reportTypeString = {
      "device-parameters": "Device Parameter List",
      "device-diagnostics": "Device Diagnostics",
    };
    const data = reportUpdateStatus.find(
      (val) => val.reportType === reportTypeString[currentRoute]
    );
    return !!data;
  }, [currentRoute, reportUpdateStatus]);

  /**Update the filterState for device */
  const updateSelectedDevice = useCallback(() => {
    const deviceList = searchBoxRef.current.getSelectedList();
    const deviceIdList = deviceList.map((list) => {
      const id = list.PK.slice(2);
      return id;
    });
    setSelectedDeviceDetails(deviceList);
    setSelectedDevice(deviceIdList.length ? deviceIdList : undefined); //Add above deviceIdList once search filter code is done
  }, []);

  /**Callback : Generate CSV */
  const onCsvClick = useCallback(() => {
    const reportType = {
      "device-diagnostics": "Device Diagnostics",
      "device-parameters": "Device Parameter List",
    };
    const queryParam = filtersApplied;
    queryParam.userTimezone = moment.tz.guess();
    queryParam.reportType = reportType[currentRoute];
    setIsLoading(true);
    generateCSVReport(queryParam)
      .then((response) => {
        setIsLoading(false);
        setShowDownloadModal(true);
      })
      .catch((e) => {
        setIsLoading(false);
      });
  }, [filtersApplied, currentRoute]);

  const updateSelectedFilters = useCallback((v) => setFiltersApplied(v), []);
  const updateReportData = useCallback((val) => setReportData(val), []);
  const updateRefreshTime = useCallback((val) => setRefreshTime(val), []);

  return (
    <div className="bodyInner combined-report-page reportPageMain">
      {siteData?.id ? (
        <div className="bodyContainer">
          {/* Header Starts  */}
          <div className="controllesWrapper header fixSubHeader">
            <div className="left-header page-heading">
              <span className="heading-1" onClick={goBack}>
                {t("reportsHeading")}
              </span>
              <span className="heading-2">
                {currentRoute === "device-parameters"
                  ? `/ ${t("deviceParameterListHeading")}`
                  : `/ ${t("deviceDiagnosticsHeading")}`}
              </span>
            </div>
            <div className="right-header">
              <div>
                <SearchBox
                  ref={searchBoxRef}
                  autoid="report-search-device"
                  placeholderText={t("deviceSearch.placeholder")}
                  titleText={t("deviceSearch.title")}
                  getOptionCallBack={() => {
                    updateSelectedDevice();
                  }}
                  isMultiSelect={true}
                  defaultSelectedVal={locationStateData?.devicePK || null}
                />
              </div>
              <span className="enabledIcon header-icons">
                <button
                  className={`verticalBtn
                    ${newDownloadNotification ? "" : "hide-circle"}`}
                  onClick={() => navigate("/reports/downloads")}
                  title={t("downloads")}
                >
                  {Icons.downalodStatusIcon}
                </button>
              </span>
              <span className="disabledIcon header-icons">
                <button
                  className={"verticalBtn"}
                  disabled
                  title={t("mark_favorite")}
                >
                  {Icons.favoriteIcon}
                </button>
              </span>
              <span className="disabledIcon header-icons">
                <button
                  className="verticalBtn"
                  disabled
                  title={t("export_as_pdf")}
                >
                  {Icons.pdfIcon}
                </button>
              </span>
              <span className="header-icons">
                <button
                  className="verticalBtn"
                  onClick={onCsvClick}
                  disabled={!reportData?.length}
                  title={t("export_as_csv")}
                >
                  {Icons.csvIcon}
                </button>
              </span>
              <span className="disabledIcon header-icons">
                <button className="verticalBtn" disabled title={t("print")}>
                  {Icons.printIcon}
                </button>
              </span>
            </div>
          </div>
          <div className="body-container-wrapper report-body">
            <div className="calendar-section">
              <DateCalendar
                getSelectedDate={(
                  timeStampRange,
                  isCustomChecker,
                  isErrorActive,
                  errorMessage
                ) => {
                  setDurationErr({
                    isErr: isErrorActive,
                    errMsg: errorMessage,
                  });
                  setSelectedDuration(timeStampRange);
                }}
              />
            </div>

            {/* Header Ends  */}
            {/* Body Starts  */}
            <div className="body-container">
              <DeviceGraphReport
                CustomToggle={CustomToggle}
                setActiveAccordion={setActiveAccordion}
                activeAccordion={activeAccordion}
                selectedDevice={selectedDevice}
                selectedDuration={selectedDuration}
                durationErr={durationErr}
                selectedDeviceDetails={selectedDeviceDetails}
              />
              <ReportListViewTab
                CustomToggle={CustomToggle}
                setActiveAccordion={setActiveAccordion}
                activeAccordion={activeAccordion}
                selectedDevice={selectedDevice}
                updateReportData={updateReportData}
                updateSelectedFilters={updateSelectedFilters}
                selectedDuration={selectedDuration}
                durationErr={durationErr}
                updateRefreshTime={updateRefreshTime}
                currentRoute={currentRoute}
              />
            </div>
            {/* Body Ends  */}
            {/* Footer Starts  */}
            <div className="footer-section">
              {reportData?.length > 0 && selectedDevice?.length ? (
                <div className="footer-time">
                  <span className="text-h4">
                    {t("reportGeneratedOn", {
                      time: formatAMPM(refreshTime, false),
                    })}{" "}
                  </span>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
          {/* Footer Ends  */}
          {isLoading && <LoadingSpinner />}
        </div>
      ) : (
        <></>
      )}
      <ModalComponent
        idName="csv-download-modal"
        isOpen={showDownloadModal}
        onCloseModal={() => setShowDownloadModal(false)}
        onConfirm={() => navigate("/reports/downloads")}
        descriptionText={t("downloadModalMsg")}
        primaryBtnText={t("goToDownload")}
        primaryTextIcon={Icons.maximizeIcon}
        secondaryBtnText={t("close")}
        needConfirmation={true}
      ></ModalComponent>
    </div>
  );
};

export default DeviceDiagnoParameterReport;

function CustomToggle({
  children,
  eventKey,
  customActiveAccordion,
  setCustomActiveAccordion,
  selectedDevice,
}) {
  useEffect(() => {
    if (selectedDevice?.length > 1) {
      let newActiveAccordion = { listViewActive: true, graphViewActive: false };
      setCustomActiveAccordion(newActiveAccordion);
    }
  }, [selectedDevice]);

  const decoratedOnClick = useAccordionButton(eventKey, () => {
    if (selectedDevice?.length > 1) {
      return;
    }
    let newActiveAccordion = {};
    if (eventKey === "list-view") {
      newActiveAccordion.listViewActive = !customActiveAccordion.listViewActive;
      newActiveAccordion.graphViewActive = true;
    } else {
      newActiveAccordion.graphViewActive =
        !customActiveAccordion.graphViewActive;
      newActiveAccordion.listViewActive = true;
    }
    setCustomActiveAccordion(newActiveAccordion);
  });

  return (
    <div
      onClick={decoratedOnClick}
      className={`toggleIcon pointer ${
        selectedDevice?.length > 1 ? "disable-toggle-icon" : ""
      }`}
    >
      {children}
    </div>
  );
}
